import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { ThemeProvider } from '@/contexts/theme-context';
import { AuthProvider, useAuth } from '@/contexts/auth-context';
import MainLayout from '@/layouts/main-layout';
import LoadingScreen from '@/components/loading-screen';
import ErrorBoundary from '@/components/error-boundary';

// Lazy loaded pages with file extensions to avoid Vite import issues
const DashboardPage = lazy(() => import('@/pages/dashboard.tsx'));
const LoginPage = lazy(() => import('@/pages/login.tsx'));
const AIAgentsPage = lazy(() => import('@/pages/ai-agents.tsx'));
const CalendarIntegrationsPage = lazy(() => import('@/pages/calendar-integrations.tsx'));
const VoiceConfigurationsPage = lazy(() => import('@/pages/voice-configurations.tsx'));
const SettingsPage = lazy(() => import('@/pages/settings.tsx'));
const NotFoundPage = lazy(() => import('@/pages/not-found.tsx'));

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider defaultTheme="light">
      <AuthProvider>
        <ErrorBoundary>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              {/* Auth routes */}
              <Route path="/login" element={<LoginPage />} />
              
              {/* Protected routes */}
              <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="ai-agents" element={<AIAgentsPage />} />
                <Route path="integrations/calendar" element={<CalendarIntegrationsPage />} />
                <Route path="voice-configurations" element={<VoiceConfigurationsPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>
              
              {/* 404 route */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          <Toaster />
        </ErrorBoundary>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
